import type { ICitationResolution } from "@/api/chat/chat.types";
import { buildRichCopyPayload } from "@/components/assistant-ui/utils/copy-as-rich-text";
import { useAui, useAuiState } from "@assistant-ui/react";
import { useCallback, useEffect, useRef } from "react";

const COPIED_DURATION_MS = 3000;
let clipboardWriteGeneration = 0;

export interface AssistantRichCopyOptions {
    chatId: string | undefined;
    resolutions: ICitationResolution[] | undefined;
}

export interface AssistantRichCopy {
    copy: () => void;
    disabled: boolean;
}

export const useAssistantRichCopy = ({
    chatId,
    resolutions,
}: AssistantRichCopyOptions): AssistantRichCopy => {
    const aui = useAui();
    const messageId = useAuiState((s) => s.message.id);
    const disabled = useAuiState((s) => {
        const streaming =
            s.message.role === "assistant" &&
            s.message.status?.type === "running";
        const hasText = s.message.parts.some(
            (part) => part.type === "text" && part.text.length > 0
        );
        return streaming || !hasText;
    });

    const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );
    const resetCopiedState = useRef<(() => void) | undefined>(undefined);
    const mounted = useRef(false);
    const scopeGeneration = useRef(0);
    const clearCopiedFeedback = useCallback(() => {
        if (copiedTimer.current !== undefined) {
            clearTimeout(copiedTimer.current);
            copiedTimer.current = undefined;
        }
        resetCopiedState.current?.();
        resetCopiedState.current = undefined;
    }, []);
    useEffect(() => {
        mounted.current = true;
        scopeGeneration.current += 1;

        return () => {
            mounted.current = false;
            scopeGeneration.current += 1;
            clearCopiedFeedback();
        };
    }, [clearCopiedFeedback, messageId]);

    const copy = useCallback(() => {
        // Resolved once, at click time. `aui.message()` answers for whichever
        // message is in scope when it is called, so a branch switch during the
        // three-second window would otherwise clear a different message's
        // checkmark than the one that was copied.
        const message = aui.message();
        const source = message.getCopyText();
        if (!source) return;

        // Undefined outright in an insecure context, which the DOM types do
        // not model. Nothing to fall back to, so the button does nothing.
        const clipboard = navigator.clipboard as Clipboard | undefined;
        if (!clipboard) return;

        // Built synchronously, before any await: a clipboard write is only
        // authorised inside the task the click started, and Safari drops one
        // that arrives after the gesture has been handed back.
        const payload = buildRichCopyPayload(source, resolutions, {
            chatId,
            appHost: window.location.origin,
        });
        const writeGeneration = ++clipboardWriteGeneration;
        const messageScope = scopeGeneration.current;
        const isCurrent = () =>
            mounted.current &&
            writeGeneration === clipboardWriteGeneration &&
            messageScope === scopeGeneration.current;

        const markCopied = () => {
            if (!isCurrent()) return;

            clearCopiedFeedback();
            message.setIsCopied(true);
            resetCopiedState.current = () => message.setIsCopied(false);
            copiedTimer.current = setTimeout(
                clearCopiedFeedback,
                COPIED_DURATION_MS
            );
        };

        const writePlain = (): Promise<void> => {
            if (!isCurrent()) return Promise.resolve();

            try {
                return clipboard
                    .writeText(payload.text)
                    .then(markCopied, () => {});
            } catch {
                return Promise.resolve();
            }
        };

        const item = richClipboardItem(payload.html, payload.text);
        if (item === undefined || typeof clipboard.write !== "function") {
            void writePlain();
            return;
        }

        try {
            void clipboard
                .write([item])
                .then(markCopied, writePlain)
                .catch(() => {});
        } catch {
            void writePlain();
        }
    }, [aui, chatId, clearCopiedFeedback, resolutions]);

    return { copy, disabled };
};

const richClipboardItem = (
    html: string | undefined,
    text: string
): ClipboardItem | undefined => {
    if (html === undefined || typeof ClipboardItem === "undefined") {
        return undefined;
    }

    try {
        return new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" }),
        });
    } catch {
        return undefined;
    }
};
